/**
 * api/projects.js — queries e mutations para o recurso /projects.
 *
 * Endpoints (Onda 3 da API real):
 *   GET    /projects                          — lista de projetos do tenant
 *   POST   /projects                          — criar novo projeto
 *   GET    /projects/{id}                     — detalhe de um projeto
 *   PATCH  /projects/{id}                     — atualizar projeto
 *   DELETE /projects/{id}                     — remover projeto
 *
 * Sub-recursos (usados pelas etapas do Wizard):
 *   GET/POST   /projects/{id}/discovery-contexts   — Step 5 (context)
 *   GET/POST   /projects/{id}/project-documents    — Step 5 (docs)
 *   GET/PUT    /projects/{id}/tech-blueprints       — Step 7 (tech)
 *   GET/POST   /projects/{id}/data-sources          — Step 6 (dataBiz) / Step 7 (dataTech)
 *   GET/PUT    /projects/{id}/ideation              — Step 4 (greenfield only)
 *
 * Convenções:
 *   - Query keys seguem hierarquia: ['projects', id?, 'sub-resource?']
 *   - Mutations invalidam a query-key pai ao concluir com sucesso.
 *   - ApiError 403 → deixa o caller renderizar <ForbiddenBanner />.
 *   - ApiError 429 → mensagem de rate-limit via error.title.
 *   - Extensível para lote 2 (import/agents): basta adicionar hooks aqui.
 *
 * Clean / SOLID:
 *   - Sem lógica de UI: apenas data-fetching e invalidação.
 *   - Um hook por caso de uso; composição no componente.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client';

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const projectKeys = {
  all:             ()       => ['projects'],
  list:            ()       => ['projects', 'list'],
  detail:          (id)     => ['projects', id],
  context:         (id)     => ['projects', id, 'discovery-contexts'],
  documents:       (id)     => ['projects', id, 'project-documents'],
  blueprint:       (id)     => ['projects', id, 'tech-blueprints'],
  dataSources:     (id)     => ['projects', id, 'data-sources'],
  ideation:        (id)     => ['projects', id, 'ideation'],
};

// ─── Queries ──────────────────────────────────────────────────────────────────

/**
 * useProjects — lista todos os projetos do tenant.
 * Usado na tela de Dashboard e no seletor do Wizard.
 */
export function useProjects() {
  const api = useApi();
  return useQuery({
    queryKey: projectKeys.list(),
    queryFn:  () => api.get('/projects'),
    staleTime: 30_000,
  });
}

/**
 * useProject — detalhe de um único projeto.
 * @param {string|null} id
 */
export function useProject(id) {
  const api = useApi();
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn:  () => api.get(`/projects/${id}`),
    enabled:  Boolean(id),
    staleTime: 30_000,
  });
}

/**
 * useDiscoveryContexts — contextos de discovery do projeto (Step 5).
 * @param {string|null} projectId
 */
export function useDiscoveryContexts(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: projectKeys.context(projectId),
    queryFn:  () => api.get(`/projects/${projectId}/discovery-contexts`),
    enabled:  Boolean(projectId),
    staleTime: 60_000,
  });
}

/**
 * useProjectDocuments — documentos do projeto (Step 5).
 * @param {string|null} projectId
 */
export function useProjectDocuments(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: projectKeys.documents(projectId),
    queryFn:  () => api.get(`/projects/${projectId}/documents`),
    enabled:  Boolean(projectId),
    staleTime: 60_000,
  });
}

/**
 * useTechBlueprint — blueprint técnico do projeto (Step 7).
 * @param {string|null} projectId
 */
export function useTechBlueprint(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: projectKeys.blueprint(projectId),
    // 404 = blueprint ainda não criado (projeto novo) → null (form vazio), não erro.
    queryFn:  async () => {
      try { return await api.get(`/projects/${projectId}/tech-blueprint`); }
      catch (e) { if (e?.status === 404) return null; throw e; }
    },
    enabled:  Boolean(projectId),
    staleTime: 60_000,
  });
}

/**
 * useDataSources — data sources do projeto (Step 6 + Step 7).
 * @param {string|null} projectId
 */
export function useDataSources(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: projectKeys.dataSources(projectId),
    // Normaliza a resposta do backend ({display_name, source_type,
    // connection_config, status}) para o shape que as telas usam ({name, type,
    // kind, doc_count}). Sem isso, nome/tipo apareciam vazios e o filtro por kind
    // dependia de sorte.
    queryFn:  async () => {
      const raw = await api.get(`/projects/${projectId}/data-sources`);
      const rows = Array.isArray(raw) ? raw : (raw?.items ?? []);
      return rows.map((r) => ({
        ...r,
        name: r.display_name ?? r.name ?? '',
        type: r.connection_config?.ui_type ?? r.source_type ?? '',
        kind: r.connection_config?.kind ?? 'business',
        doc_count: r.connection_config?.doc_count ?? r.indexed_docs ?? undefined,
      }));
    },
    enabled:  Boolean(projectId),
    staleTime: 60_000,
  });
}

/**
 * useIdeation — dados de ideação (Step 4, só greenfield).
 * @param {string|null} projectId
 */
export function useIdeation(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: projectKeys.ideation(projectId),
    // 404 = ideação ainda não criada (projeto novo) → null (form vazio), não erro.
    queryFn:  async () => {
      try { return await api.get(`/projects/${projectId}/ideation`); }
      catch (e) { if (e?.status === 404) return null; throw e; }
    },
    enabled:  Boolean(projectId),
    staleTime: 60_000,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

/**
 * useCreateProject — cria um novo projeto.
 * Invalida a lista de projetos ao concluir.
 *
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useCreateProject() {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => api.post('/projects', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.list() });
    },
  });
}

/**
 * useUpdateProject — atualiza um projeto existente via PATCH.
 * @param {string} projectId
 */
export function useUpdateProject(projectId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => api.patch(`/projects/${projectId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.list() });
    },
  });
}

/**
 * useUpsertDiscoveryContext — cria ou atualiza o contexto de discovery (Step 5).
 * @param {string} projectId
 */
export function useUpsertDiscoveryContext(projectId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => api.post(`/projects/${projectId}/discovery-contexts`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.context(projectId) });
    },
  });
}

/**
 * useAddProjectDocument — adiciona um documento ao projeto (Step 5).
 * @param {string} projectId
 */
export function useAddProjectDocument(projectId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => api.post(`/projects/${projectId}/documents`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.documents(projectId) });
    },
  });
}

/**
 * useDeleteProjectDocument — exclui um documento do projeto (Contexto & Discovery).
 * Soft-delete no backend + purga dos chunks RAG do documento.
 * @param {string} projectId
 */
export function useDeleteProjectDocument(projectId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId) => api.delete(`/projects/${projectId}/documents/${documentId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.documents(projectId) });
    },
  });
}

/** useAddDocumentFromUrl — ingesta uma fonte de negócio por URL (ex.: Swagger). */
export function useAddDocumentFromUrl(projectId) {
  const api         = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/projects/${projectId}/documents/from-url`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.documents(projectId) });
    },
  });
}

/** useSuggestBusinessScope — pede à IA um escopo de negócio a partir das fontes. */
export function useSuggestBusinessScope(projectId) {
  const api = useApi();
  return useMutation({
    mutationKey: ['ai'],
    meta: { aiLabel: 'A IA está inferindo o escopo de negócio…', aiSublabel: 'Lendo a Ideação e as APIs/documentos indexados para descrever as capacidades de negócio.' },
    mutationFn: () => api.post(`/projects/${projectId}/business-scope/suggest`, {}),
  });
}

/**
 * useUpsertTechBlueprint — cria ou atualiza o blueprint técnico (Step 7).
 * @param {string} projectId
 */
export function useUpsertTechBlueprint(projectId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => api.put(`/projects/${projectId}/tech-blueprint`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.blueprint(projectId) });
    },
  });
}

/**
 * useUpsertIdeation — cria ou atualiza a ideação do projeto (Step 4).
 * @param {string} projectId
 */
export function useUpsertIdeation(projectId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => api.put(`/projects/${projectId}/ideation`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.ideation(projectId) });
    },
  });
}

/**
 * useAddDataSource — adiciona uma fonte de dados (Step 6 / Step 7).
 * @param {string} projectId
 */
// Tipos de UI → source_type aceito pelo backend (regex/CHECK). Os demais
// (swagger/sharepoint/outros/business) caem em 'other' — o rótulo real fica no
// display_name e o tipo de UI é preservado em connection_config.ui_type.
const DS_BACKEND_TYPES = new Set(['confluence', 'notion', 'jira', 'github_issues', 'repo_knowledge_pack', 'upload', 'azure_devops', 'other']);
function toBackendSourceType(t) {
  return DS_BACKEND_TYPES.has(t) ? t : 'other';
}

export function useAddDataSource(projectId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    // O front usa {name, type, kind, doc_count}; o backend exige
    // {source_type, display_name, connection_config}. Mapeia aqui (antes dava 422).
    mutationFn: (body) => api.post(`/projects/${projectId}/data-sources`, {
      source_type: toBackendSourceType(body.type),
      display_name: (body.name || 'Fonte de negócio').slice(0, 255),
      connection_config: {
        ui_type: body.type || 'other',
        kind: body.kind || 'business',
        ...(body.doc_count != null ? { doc_count: body.doc_count } : {}),
        ...(body.note ? { note: String(body.note).slice(0, 4000) } : {}),
      },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.dataSources(projectId) });
    },
  });
}
